import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/navigation/AdminSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import StatCard from '../admin-control-panel/components/StatCard';
import QuickActionCard from './components/QuickActionCard';
import SystemHealthCard from './components/SystemHealthCard';
import ActivitySummaryCard from './components/ActivitySummaryCard';
import AlertCard from './components/AlertCard';
import { authService } from '../../services/authService';


const AdminPortalDashboard = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const session = await authService?.getCurrentSession();
        if (!session) {
          navigate('/admin-portal-login');
        }
      } catch (error) {
        navigate('/admin-portal-login');
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={32} className="animate-spin text-primary mx-auto mb-2" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const kpiMetrics = [
    {
      title: 'Active Users',
      value: '0',
      change: '+0%',
      changeType: 'neutral',
      icon: 'Users',
      iconBg: 'bg-gradient-to-br from-primary to-secondary'
    },
    {
      title: 'Pending Approvals',
      value: '0',
      change: '+0',
      changeType: 'neutral',
      icon: 'Clock',
      iconBg: 'bg-gradient-to-br from-warning to-warning/80'
    },
    {
      title: 'Match Success Rate',
      value: '0%',
      change: '+0%',
      changeType: 'neutral',
      icon: 'TrendingUp',
      iconBg: 'bg-gradient-to-br from-success to-success/80'
    },
    {
      title: 'Active Subscriptions',
      value: '0',
      change: '+0%',
      changeType: 'neutral',
      icon: 'CreditCard',
      iconBg: 'bg-gradient-to-br from-accent to-accent/80'
    }
  ];

  const quickActions = [
    {
      title: 'Identity Reveals',
      description: '0 pending approvals',
      icon: 'Shield',
      priority: 'normal',
      action: () => navigate('/admin-control-panel#reveal-approvals')
    },
    {
      title: 'User Verification',
      description: '0 requests waiting',
      icon: 'UserCheck',
      priority: 'normal',
      action: () => navigate('/admin-control-panel#access-requests')
    },
    {
      title: 'Compliance Alerts',
      description: '0 items need review',
      icon: 'AlertTriangle',
      priority: 'normal',
      action: () => navigate('/admin-control-panel#reported')
    },
    {
      title: 'User Management',
      description: 'Manage candidates & employers',
      icon: 'Users',
      priority: 'normal',
      action: () => navigate('/admin-control-panel#candidates')
    }
  ];

  const systemHealth = [
    { name: 'API Server', status: 'operational', uptime: '99.98%', responseTime: '45ms' },
    { name: 'Database', status: 'operational', uptime: '99.99%', responseTime: '12ms' },
    { name: 'Storage', status: 'operational', uptime: '100%', usage: '0%' },
    { name: 'Email Service', status: 'operational', uptime: '100%', responseTime: '50ms' }
  ];

  const recentActivity = [];

  const criticalAlerts = [
    {
      id: 1,
      type: 'security',
      title: 'Multiple failed login attempts detected',
      description: 'IP: 192.168.1.100 - 5 failed attempts in 10 minutes',
      time: '15 min ago',
      priority: 'high'
    },
    {
      id: 2,
      type: 'compliance',
      title: 'Identity reveal pending approval',
      description: '3 requests waiting over 24 hours',
      time: '1 hour ago',
      priority: 'high'
    },
    {
      id: 3,
      type: 'system',
      title: 'Email service performance degraded',
      description: 'Response time increased by 180%',
      time: '2 hours ago',
      priority: 'medium'
    }
  ];

  return (
    <div className="flex min-h-screen bg-background">
      <AdminSidebar
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />

      <div className="flex-1 md:ml-64">
        {/* Header */}
        <div className="bg-card border-b border-border px-4 md:px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-semibold text-foreground">Admin Dashboard</h1>
              <p className="text-sm text-muted-foreground mt-1">
                {currentTime?.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" iconName="RefreshCw" size="sm">
                Refresh
              </Button>
              <Button variant="outline" iconName="LogOut" size="sm" onClick={() => navigate('/admin-portal-login')}>
                Logout
              </Button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-4 md:p-6 space-y-6">
          {/* KPI Metrics */}
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-4">Key Performance Indicators</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {kpiMetrics?.map((metric, index) => (
                <StatCard key={index} {...metric} />
              ))}
            </div>
          </div>

          {/* Critical Alerts */}
          {criticalAlerts?.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-foreground">Critical Alerts</h2>
                <Button variant="ghost" size="sm" iconName="Bell">
                  View All
                </Button>
              </div>
              <div className="space-y-3">
                {criticalAlerts?.map((alert) => (
                  <AlertCard key={alert?.id} alert={alert} />
                ))}
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div>
            <h2 className="text-lg font-semibold text-foreground mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {quickActions?.map((action, index) => (
                <QuickActionCard key={index} action={action} />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* System Health */}
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-4">System Health</h2>
              <div className="bg-card rounded-2xl p-6 elevation-sm space-y-4">
                {systemHealth?.map((system, index) => (
                  <SystemHealthCard key={index} system={system} />
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-4">Recent Activity</h2>
              <div className="bg-card rounded-2xl p-6 elevation-sm space-y-4">
                {recentActivity?.map((activity, index) => (
                  <ActivitySummaryCard key={index} activity={activity} />
                ))}
              </div>
            </div>
          </div>

          {/* Platform Analytics Summary */}
          <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl p-6 elevation-sm">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Platform Analytics</h2>
                <p className="text-sm text-muted-foreground">Last 30 days overview</p>
              </div>
              <Button variant="outline" iconName="BarChart3" onClick={() => navigate('/admin-control-panel#analytics')}>
                View Details
              </Button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-card rounded-xl p-4">
                <p className="text-sm text-muted-foreground mb-1">Total Matches</p>
                <p className="text-2xl font-semibold text-foreground">1,247</p>
              </div>
              <div className="bg-card rounded-xl p-4">
                <p className="text-sm text-muted-foreground mb-1">New Registrations</p>
                <p className="text-2xl font-semibold text-foreground">384</p>
              </div>
              <div className="bg-card rounded-xl p-4">
                <p className="text-sm text-muted-foreground mb-1">Revenue</p>
                <p className="text-2xl font-semibold text-foreground">$42.8K</p>
              </div>
              <div className="bg-card rounded-xl p-4">
                <p className="text-sm text-muted-foreground mb-1">Engagement Rate</p>
                <p className="text-2xl font-semibold text-foreground">94.2%</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPortalDashboard;